import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import ChatService from '../services/ChatService';
import { errorText } from '../utils/format';

const LIST_POLL_MS = 6000;
const IDLE_POLL_MS = 6000;
const WORKING_POLL_MS = 1500;

/** Conversation list (bots and group chats) and what is connected. Refreshes quietly in the background. */
export function useConversationsViewModel() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const alive = useRef(true);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) { setLoading(true); setError(null); }
    try {
      const [list, st] = await Promise.all([ChatService.conversations(), ChatService.status()]);
      if (!alive.current) return;
      setItems(list);
      setStatus(st);
      setError(null);
    } catch (err) {
      if (alive.current && !silent) setError(err);
    } finally {
      if (alive.current && !silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    const timer = setInterval(() => load({ silent: true }), LIST_POLL_MS);
    return () => { alive.current = false; clearInterval(timer); };
  }, [load]);

  const markReadLocally = useCallback((id) => {
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
  }, []);

  return {
    items, status, loading, error, reload: load, markReadLocally,
  };
}

/** One open conversation: messages, who is working, sending and stopping. */
export function useChatViewModel(conversationId, { onActivity } = {}) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const current = useRef(conversationId);
  const lastCount = useRef(0);

  const fetchMessages = useCallback(async () => {
    const data = await ChatService.messages(conversationId);
    if (current.current !== conversationId) return null;
    setMessages(data.messages);
    setActiveRun(data.active_run);
    return data;
  }, [conversationId]);

  const markRead = useCallback(async () => {
    try { await ChatService.markRead(conversationId); } catch (e) { /* not critical */ }
    if (onActivity) onActivity(conversationId);
  }, [conversationId, onActivity]);

  useEffect(() => {
    current.current = conversationId;
    lastCount.current = 0;
    setConversation(null);
    setMessages([]);
    setActiveRun(null);
    setError(null);
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const [conv, data] = await Promise.all([ChatService.conversation(conversationId), ChatService.messages(conversationId)]);
        if (cancelled) return;
        setConversation(conv);
        setMessages(data.messages);
        setActiveRun(data.active_run);
        lastCount.current = data.messages.length;
        markRead();
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId, markRead]);

  // Keep the chat live: fast while the team is working, slow otherwise.
  useEffect(() => {
    if (loading || error) return undefined;
    const timer = setTimeout(async () => {
      try {
        const data = await fetchMessages();
        if (data && data.messages.length > lastCount.current) {
          lastCount.current = data.messages.length;
          markRead();
        }
      } catch (e) { /* try again on the next tick */ }
    }, activeRun ? WORKING_POLL_MS : IDLE_POLL_MS);
    return () => clearTimeout(timer);
  }, [loading, error, activeRun, messages, fetchMessages, markRead]);

  const send = async (text) => {
    const body = text.trim();
    if (!body) return false;
    setSending(true);
    setSendError('');
    const optimistic = {
      id: `local-${Date.now()}`, kind: 'text', content: body, created_at: new Date().toISOString(), sender: { type: 'human' }, pending: true, meta: null,
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      await ChatService.send(conversationId, body);
      await fetchMessages();
      return true;
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setSendError(errorText(err));
      return false;
    } finally {
      setSending(false);
    }
  };

  const stop = async () => {
    if (!activeRun) return;
    try { await ChatService.stop(activeRun.id); } catch (err) { setSendError(errorText(err)); }
  };

  return {
    conversation,
    messages,
    activeRun,
    loading,
    error,
    sending,
    sendError,
    clearSendError: () => setSendError(''),
    send,
    stop,
    refresh: fetchMessages,
  };
}
